/**
 * Contact Loan Service
 * Manages loans associated with contacts - both pre-qual and Encompass linked loans
 */

import { supabase } from '@/utils/supabase';

// Types
export interface ContactLoan {
  id: string;
  contactId: string;
  leadId?: string;
  encompassLoanId?: string;
  loanType: 'pre_qual' | 'encompass';
  loanPurpose?: string;
  propertyType?: string;
  loanAmount?: number;
  propertyValue?: number;
  propertyAddress?: string;
  propertyCity?: string;
  propertyState?: string;
  propertyZip?: string;
  loanNumber?: string;
  status?: string;
  isPrimary: boolean;
  notes?: string;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateContactLoanInput {
  contactId: string;
  loanType: 'pre_qual' | 'encompass';
  encompassLoanId?: string;
  loanPurpose?: string;
  propertyType?: string;
  loanAmount?: number;
  propertyValue?: number;
  propertyAddress?: string;
  propertyCity?: string;
  propertyState?: string;
  propertyZip?: string;
  loanNumber?: string;
  status?: string;
  isPrimary?: boolean;
  notes?: string;
}

export interface UpdateContactLoanInput {
  loanPurpose?: string;
  propertyType?: string;
  loanAmount?: number;
  propertyValue?: number;
  propertyAddress?: string;
  propertyCity?: string;
  propertyState?: string;
  propertyZip?: string;
  status?: string;
  isPrimary?: boolean;
  notes?: string;
}

// Encompass loan from database (for search results)
export interface EncompassLoanResult {
  encompassLoanId: string;
  loanNumber?: string;
  borrowerFirstName?: string;
  borrowerLastName?: string;
  borrowerEmail?: string;
  loanAmount?: number;
  loanPurpose?: string;
  propertyAddress?: string;
  propertyCity?: string;
  propertyState?: string;
  status?: string;
}

class ContactLoanService {
  /**
   * Map database row to ContactLoan type
   */
  private mapToContactLoan(row: Record<string, unknown>): ContactLoan {
    return {
      id: row.id as string,
      contactId: row.contact_id as string,
      leadId: row.lead_id as string | undefined,
      encompassLoanId: row.encompass_loan_id as string | undefined,
      loanType: row.loan_type as 'pre_qual' | 'encompass',
      loanPurpose: row.loan_purpose as string | undefined,
      propertyType: row.property_type as string | undefined,
      loanAmount: row.loan_amount ? Number(row.loan_amount) : undefined,
      propertyValue: row.property_value ? Number(row.property_value) : undefined,
      propertyAddress: row.property_address as string | undefined,
      propertyCity: row.property_city as string | undefined,
      propertyState: row.property_state as string | undefined,
      propertyZip: row.property_zip as string | undefined,
      loanNumber: row.loan_number as string | undefined,
      status: row.status as string | undefined,
      isPrimary: row.is_primary as boolean,
      notes: row.notes as string | undefined,
      organizationId: row.organization_id as string,
      createdAt: row.created_at as string,
      updatedAt: row.updated_at as string
    };
  }

  /**
   * Get all loans for a contact
   */
  async getLoansForContact(contactId: string): Promise<ContactLoan[]> {
    const { data, error } = await supabase
      .from('contact_loans')
      .select('*')
      .eq('contact_id', contactId)
      .order('is_primary', { ascending: false })
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Failed to get contact loans:', error);
      throw error;
    }

    return (data || []).map(this.mapToContactLoan);
  }

  /**
   * Get a single loan by ID
   */
  async getLoanById(loanId: string): Promise<ContactLoan | null> {
    const { data, error } = await supabase
      .from('contact_loans')
      .select('*')
      .eq('id', loanId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
return null;
} // Not found
      throw error;
    }

    return data ? this.mapToContactLoan(data) : null;
  }

  /**
   * Get primary loan for a contact
   */
  async getPrimaryLoan(contactId: string): Promise<ContactLoan | null> {
    const { data, error } = await supabase
      .from('contact_loans')
      .select('*')
      .eq('contact_id', contactId)
      .eq('is_primary', true)
      .maybeSingle();

    if (error) {
      throw error;
    }

    return data ? this.mapToContactLoan(data) : null;
  }

  /**
   * Create a new loan for a contact
   */
  async createLoan(input: CreateContactLoanInput): Promise<ContactLoan> {
    // Get organization ID from current user
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
throw new Error('No authenticated user');
}

    const { data: userProfile } = await supabase
      .from('users')
      .select('organization_id')
      .eq('id', user.id)
      .single();

    if (!userProfile) {
throw new Error('User profile not found');
}

    // Check if this is the first loan (should be primary)
    const existingLoans = await this.getLoansForContact(input.contactId);
    const isPrimary = input.isPrimary ?? existingLoans.length === 0;

    const { data, error } = await supabase
      .from('contact_loans')
      .insert({
        contact_id: input.contactId,
        loan_type: input.loanType,
        encompass_loan_id: input.encompassLoanId,
        loan_purpose: input.loanPurpose,
        property_type: input.propertyType,
        loan_amount: input.loanAmount,
        property_value: input.propertyValue,
        property_address: input.propertyAddress,
        property_city: input.propertyCity,
        property_state: input.propertyState,
        property_zip: input.propertyZip,
        loan_number: input.loanNumber,
        status: input.status || 'new',
        is_primary: isPrimary,
        notes: input.notes,
        organization_id: userProfile.organization_id
      })
      .select()
      .single();

    if (error) {
      console.error('Failed to create contact loan:', error);
      throw error;
    }

    return this.mapToContactLoan(data);
  }

  /**
   * Update a loan
   */
  async updateLoan(loanId: string, input: UpdateContactLoanInput): Promise<ContactLoan> {
    const updateData: Record<string, unknown> = {};

    if (input.loanPurpose !== undefined) {
updateData.loan_purpose = input.loanPurpose;
}
    if (input.propertyType !== undefined) {
updateData.property_type = input.propertyType;
}
    if (input.loanAmount !== undefined) {
updateData.loan_amount = input.loanAmount;
}
    if (input.propertyValue !== undefined) {
updateData.property_value = input.propertyValue;
}
    if (input.propertyAddress !== undefined) {
updateData.property_address = input.propertyAddress;
}
    if (input.propertyCity !== undefined) {
updateData.property_city = input.propertyCity;
}
    if (input.propertyState !== undefined) {
updateData.property_state = input.propertyState;
}
    if (input.propertyZip !== undefined) {
updateData.property_zip = input.propertyZip;
}
    if (input.status !== undefined) {
updateData.status = input.status;
}
    if (input.isPrimary !== undefined) {
updateData.is_primary = input.isPrimary;
}
    if (input.notes !== undefined) {
updateData.notes = input.notes;
}

    const { data, error } = await supabase
      .from('contact_loans')
      .update(updateData)
      .eq('id', loanId)
      .select()
      .single();

    if (error) {
      console.error('Failed to update contact loan:', error);
      throw error;
    }

    return this.mapToContactLoan(data);
  }

  /**
   * Delete a loan
   */
  async deleteLoan(loanId: string): Promise<void> {
    const { error } = await supabase
      .from('contact_loans')
      .delete()
      .eq('id', loanId);

    if (error) {
      console.error('Failed to delete contact loan:', error);
      throw error;
    }
  }

  /**
   * Set a loan as primary (will unset other primary loans for the contact)
   */
  async setPrimaryLoan(loanId: string): Promise<ContactLoan> {
    return this.updateLoan(loanId, { isPrimary: true });
  }

  /**
   * Search Encompass loans by borrower name/email
   * Returns loans from the encompass_loans table that match the criteria
   */
  async searchEncompassLoans(criteria: {
    firstName?: string;
    lastName?: string;
    email?: string;
  }): Promise<EncompassLoanResult[]> {
    let query = supabase
      .from('encompass_loans')
      .select('*')
      .limit(20);

    // Build search conditions
    const conditions: string[] = [];

    if (criteria.firstName) {
      conditions.push(`borrower_first_name.ilike.%${criteria.firstName}%`);
    }
    if (criteria.lastName) {
      conditions.push(`borrower_last_name.ilike.%${criteria.lastName}%`);
    }
    if (criteria.email) {
      conditions.push(`borrower_email.ilike.%${criteria.email}%`);
    }

    if (conditions.length > 0) {
      query = query.or(conditions.join(','));
    }

    const { data, error } = await query.order('updated_at', { ascending: false });

    if (error) {
      console.error('Failed to search Encompass loans:', error);
      throw error;
    }

    return (data || []).map(row => ({
      encompassLoanId: row.encompass_loan_id,
      loanNumber: row.loan_number,
      borrowerFirstName: row.borrower_first_name,
      borrowerLastName: row.borrower_last_name,
      borrowerEmail: row.borrower_email,
      loanAmount: row.trans_details_loan_amt ? Number(row.trans_details_loan_amt) : undefined,
      loanPurpose: row.trans_details_loan_purpose,
      propertyAddress: row.property_address,
      propertyCity: row.property_city,
      propertyState: row.property_state,
      status: row.loan_status
    }));
  }

  /**
   * Get auto-suggested Encompass loans for a contact
   * Based on contact's name and email
   */
  async getAutoSuggestedLoans(contact: {
    firstName?: string;
    lastName?: string;
    email?: string;
  }): Promise<EncompassLoanResult[]> {
    return this.searchEncompassLoans({
      firstName: contact.firstName,
      lastName: contact.lastName,
      email: contact.email
    });
  }

  /**
   * Link an Encompass loan to a contact
   */
  async linkEncompassLoan(
    contactId: string,
    encompassLoan: EncompassLoanResult,
    isPrimary?: boolean
  ): Promise<ContactLoan> {
    return this.createLoan({
      contactId,
      loanType: 'encompass',
      encompassLoanId: encompassLoan.encompassLoanId,
      loanPurpose: encompassLoan.loanPurpose,
      loanAmount: encompassLoan.loanAmount,
      propertyAddress: encompassLoan.propertyAddress,
      propertyCity: encompassLoan.propertyCity,
      propertyState: encompassLoan.propertyState,
      loanNumber: encompassLoan.loanNumber,
      status: encompassLoan.status,
      isPrimary
    });
  }

  /**
   * Check if an Encompass loan is already linked to a contact
   */
  async isEncompassLoanLinked(contactId: string, encompassLoanId: string): Promise<boolean> {
    const { data, error } = await supabase
      .from('contact_loans')
      .select('id')
      .eq('contact_id', contactId)
      .eq('encompass_loan_id', encompassLoanId)
      .maybeSingle();

    if (error) {
      throw error;
    }

    return !!data;
  }
}

export const contactLoanService = new ContactLoanService();
